import { useMemo } from "react";
import { doc } from "firebase/firestore";
import type { User } from "firebase/auth";

import { useAuth } from "@/firebase/useAuth";
import { db } from "@/firebase/firebase-config";
import { useFirestoreQuery } from "./useFirestoreQuery";

export type UserStats = {
  // Legacy global XP value (if present)
  xp?: number;
  // New explicit global XP total
  xpTotal?: number;
  level?: number;
  completedMissions?: number;
  totalEarnings?: number;
  followers?: number;
  // Future-friendly map of XP by domainId (e.g. "media-literacy")
  xpByDomain?: Record<string, number>;
};

export type StatsState = {
  stats: (UserStats & { id?: string }) | null;
  loading: boolean;
  error: string | null;
};

export function useUserStats(explicitUser?: User | null): StatsState {
  const { user, loading: authLoading } = useAuth();
  const activeUser = explicitUser ?? user;

  const statsDoc = useMemo(
    () =>
      activeUser
        ? doc(db(), "users", activeUser.uid, "meta", "stats")
        : undefined,
    [activeUser]
  );

  const { data, loading, error } = useFirestoreQuery<UserStats>(statsDoc);

  return {
    stats: (data as UserStats & { id?: string }) ?? null,
    loading: authLoading || loading,
    error,
  };
}
